/**
 * ContractFieldOverlay — 편집기(ContractRichEditor) 위에 얹히는 필드 박스 오버레이.
 *
 * <p>편집기와 동일한 좌표계·zoom 을 공유해 화면 배경(문서)이 Step 1 본문편집과 완전 동일하게
 * 보이면서, 여기서는 필드 배치만 가능. `ContractRichEditor.overlay` prop 으로 주입됨.
 */
import { useEffect, useRef, useState } from "react";
import type { ContractField, FieldType } from "./contractTemplate";
import { A4 } from "./editor/coords";
import { fieldColor, fieldDefaultBox, fieldIcon } from "./editor/nodes";
import { useSealList } from "~/services/business/seal";

interface Props {
  fields: ContractField[];
  onChange: (fields: ContractField[]) => void;
  /** 페이지 개수 — 필드 페이지 오프셋 계산용. */
  pageCount: number;
  /** 편집기 현재 zoom (0.25~3) 을 가져오는 함수 — 드래그 좌표 보정용. */
  getZoom: () => number;
  disabled?: boolean;
  /** 툴바 렌더 스킵 여부. true 일 때는 필드 박스만 렌더 (툴바는 부모가 별도 렌더). */
  hideToolbar?: boolean;
}

/** 필드 배치 툴바 — 페이지 상단에 sticky 로 위치. Step 2 부모에서 별도 마운트. */
export function ContractFieldToolbar({
  onAdd,
  disabled = false,
}: {
  onAdd: (type: FieldType) => void;
  disabled?: boolean;
}) {
  if (disabled) return null;
  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        padding: "6px 10px",
        background: "#fff",
        borderBottom: "1px solid #E9E9E9",
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      <span style={{ fontSize: 11, color: "#999", marginRight: 4 }}>
        필드 추가:
      </span>
      {FIELD_TOOLS.map((tool) => (
        <button
          key={tool.type}
          type="button"
          // 에디터의 커서(선택) 를 잃지 않도록 mousedown 기본 동작(포커스 이동) 차단
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onAdd(tool.type)}
          style={{
            padding: "4px 10px",
            fontSize: 12,
            fontWeight: 600,
            background: "#fff",
            border: `1px solid ${fieldColor(tool.type)}`,
            color: fieldColor(tool.type),
            borderRadius: 999,
            cursor: "pointer",
          }}
        >
          + {tool.label}
        </button>
      ))}
    </div>
  );
}

/** 페이지 사이 gap (px). ContractRichEditor 의 contractPage margin 과 일치. */
const PAGE_GAP = 12;

const FIELD_TOOLS: { type: FieldType; label: string }[] = [
  { type: "signature", label: "보호자 서명" },
  { type: "seal", label: "사업장 도장" },
  { type: "checkbox", label: "체크박스" },
  { type: "address", label: "주소" },
  { type: "text", label: "텍스트" },
  { type: "longText", label: "긴 텍스트" },
  { type: "date", label: "날짜" },
];

const defaultKey = (type: FieldType) => {
  const stamp = Date.now();
  if (type === "signature") return "guardianSign";
  if (type === "address") return "guardianAddress";
  if (type === "date") return `fillDate_${stamp}`;
  if (type === "seal") return "companySeal";
  if (type === "checkbox") return `checkbox_${stamp}`;
  if (type === "longText") return `longText_${stamp}`;
  return `text_${stamp}`;
};

const defaultLabel = (type: FieldType) =>
  FIELD_TOOLS.find((t) => t.type === type)?.label ?? "필드";

/** 페이지 로컬 좌표 → 캔버스 전역 Y. */
const pageYToCanvasY = (pageNo: number, y: number) =>
  (pageNo - 1) * (A4.HEIGHT_PX + PAGE_GAP) + y;

export function ContractFieldOverlay({
  fields,
  onChange,
  pageCount,
  getZoom,
  disabled = false,
}: Props) {
  // 사업장 도장 필드 미리보기 — 기본(default)으로 설정된 활성 도장 이미지를 필드에 표시.
  // 기본 도장이 없으면 이미지는 안 뜬다(발송 시 찍히는 그 도장을 미리 보여주는 용도).
  const { data: sealList = [] } = useSealList();
  const activeSeals = sealList.filter(
    (s) => s.activeYn === "Y" && !!s.imageUrl,
  );
  const defaultSeal = activeSeals.find((s) => s.defaultYn === "Y") ?? null;
  /**
   * 도장 필드에 실제로 찍힐 도장 — 필드에 선택 저장(defaultValue=sealSeq)된 도장이 있으면 그걸,
   * 없으면 기본 도장. 사장님이 필드설정에서 도장을 바꿀 수 있고, 기본값은 "기본 설정 도장".
   */
  const resolveSeal = (f: ContractField) =>
    activeSeals.find((s) => String(s.sealSeq) === f.defaultValue) ??
    defaultSeal;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  // 도장 선택 팝업 대상 필드 id
  const [sealPickerFor, setSealPickerFor] = useState<string | null>(null);
  const [drag, setDrag] = useState<null | {
    mode: "move" | "resize";
    id: string;
    startClientX: number;
    startClientY: number;
    baseX: number;
    baseY: number;
    baseW: number;
    baseH: number;
    basePageNo: number;
  }>(null);
  const dragRef = useRef(drag);
  useEffect(() => {
    dragRef.current = drag;
  }, [drag]);

  // 드래그 핸들러가 항상 최신 fields 를 보도록 ref 로 미러링 (stale closure 방지)
  const fieldsRef = useRef(fields);
  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // 필드 바깥(문서 본문·툴바 등)을 클릭하면 선택 해제 → 필수/선택 토글도 닫힘.
  // 필드 박스와 그 안의 토글은 data-field-id 로 감싸져 있어 여기서 제외된다.
  useEffect(() => {
    if (!selectedId) return;
    const onDocPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest("[data-field-id]")) return;
      setSelectedId(null);
    };
    document.addEventListener("pointerdown", onDocPointerDown);
    return () => document.removeEventListener("pointerdown", onDocPointerDown);
  }, [selectedId]);

  // NOTE: FE DOM 기반 자동 필드 재배치는 제거됨 —
  // BE 의 결정론 필드 배치(스트림 4단계, Playwright DOM 실측)가 좌표 권위자.
  // FE 재배치는 "날인일로부터" 같은 서술문 오탐으로 중복 필드를 만들던 부작용이 있었음.

  const deleteField = (id: string) => {
    if (disabled) return;
    onChange(fields.filter((f) => f.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  // 드래그 중에도 항상 최신 fields 를 base 로 patch (ref 기반 — stale closure 방지)
  const updateField = (id: string, patch: Partial<ContractField>) => {
    onChangeRef.current(
      fieldsRef.current.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    );
  };

  const rootRef = useRef<HTMLDivElement | null>(null);
  /** 클릭 vs 드래그 판정 임계값 (px). 이 미만 이동 = 클릭. */
  const CLICK_THRESHOLD = 4;

  /**
   * 오버레이 컨테이너의 실제 DOM 스케일을 측정.
   * PanZoomCanvas 가 transform: scale() 을 적용하므로 clientWidth 는 원본 그대로지만,
   * getBoundingClientRect().width 는 실제 렌더링된 픽셀. 두 값의 비율이 실제 zoom.
   */
  const measureActualZoom = (): number => {
    const el = rootRef.current;
    if (!el) return 1;
    const rect = el.getBoundingClientRect();
    const naturalWidth = el.offsetWidth || A4.WIDTH_PX;
    if (naturalWidth === 0) return 1;
    const z = rect.width / naturalWidth;
    return z > 0 ? z : 1;
  };

  // 드래그 처리 — pointerdown 에서 리스너를 "동기적으로" 부착한다.
  // (기존엔 setDrag → 리렌더 → useEffect 로 window 리스너를 붙여서, 그 사이
  //  빠르게 들어온 pointermove 이벤트가 유실되어 실제 마우스 드래그가 먹지 않았음.)
  const applyDragMove = (e: PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const rawDx = e.clientX - d.startClientX;
    const rawDy = e.clientY - d.startClientY;
    // 클릭 vs 드래그 판정 — 임계값(4px) 미만은 무시 (모바일 실수 방지)
    if (Math.abs(rawDx) < CLICK_THRESHOLD && Math.abs(rawDy) < CLICK_THRESHOLD)
      return;
    // getZoom(state) 대신 DOM 실측 스케일 → 초기 fit-to-screen 지연 문제 회피
    const zoom = measureActualZoom();
    const dx = rawDx / zoom;
    const dy = rawDy / zoom;
    if (d.mode === "move") {
      // 가로: 페이지 폭 안에서 클램프
      const nx = Math.max(0, Math.min(A4.WIDTH_PX - d.baseW, d.baseX + dx));
      // 세로: 페이지 스택 전역 Y 로 변환 → 어느 페이지에 속하는지 계산 → 로컬 y 로 환산
      const pageStride = A4.HEIGHT_PX + PAGE_GAP;
      const canvasY = (d.basePageNo - 1) * pageStride + d.baseY + dy;
      // 전체 캔버스 안 클램프 (0 ~ 마지막 페이지 하단)
      const maxCanvasY =
        Math.max(1, pageCount) * pageStride - PAGE_GAP - d.baseH;
      const clampedCanvasY = Math.max(0, Math.min(maxCanvasY, canvasY));
      // 새 pageNo · 로컬 y 산출 (필드 상단이 어느 페이지 안에 있는지)
      let newPageNo = Math.floor(clampedCanvasY / pageStride) + 1;
      newPageNo = Math.max(1, Math.min(Math.max(1, pageCount), newPageNo));
      let newY = clampedCanvasY - (newPageNo - 1) * pageStride;
      // 페이지 사이 gap 구간에 걸치면 그 페이지 하단으로 스냅
      if (newY > A4.HEIGHT_PX - d.baseH) newY = A4.HEIGHT_PX - d.baseH;
      if (newY < 0) newY = 0;
      updateField(d.id, {
        x: Math.round(nx),
        y: Math.round(newY),
        pageNo: newPageNo,
      });
    } else {
      // 리사이즈: 최소 20 · 페이지 밖으로 못 나가게
      const nw = Math.max(20, Math.min(A4.WIDTH_PX - d.baseX, d.baseW + dx));
      const nh = Math.max(20, Math.min(A4.HEIGHT_PX - d.baseY, d.baseH + dy));
      updateField(d.id, { width: Math.round(nw), height: Math.round(nh) });
    }
  };
  // 리스너 참조를 ref 로 들고 있다가 pointerup/cancel 에서 정확히 해제
  const dragListenersRef = useRef<{
    move: (e: PointerEvent) => void;
    up: (e: PointerEvent) => void;
  } | null>(null);

  const detachDragListeners = () => {
    const l = dragListenersRef.current;
    if (!l) return;
    window.removeEventListener("pointermove", l.move);
    window.removeEventListener("pointerup", l.up);
    window.removeEventListener("pointercancel", l.up);
    dragListenersRef.current = null;
  };

  const beginDrag = (e: React.PointerEvent, d: NonNullable<typeof drag>) => {
    dragRef.current = d;
    setDrag(d);
    // 손이 필드 밖으로 나가도 계속 이벤트 받도록 pointer capture (실패해도 window 리스너로 커버)
    try {
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
    // 이전 드래그가 남아 있으면 정리
    detachDragListeners();
    const move = (ev: PointerEvent) => applyDragMove(ev);
    const up = () => {
      setDrag(null);
      dragRef.current = null;
      detachDragListeners();
    };
    dragListenersRef.current = { move, up };
    // ★ 동기 부착 — 리렌더를 기다리지 않으므로 첫 pointermove 부터 반영됨
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  };

  // 언마운트 시 잔여 리스너 정리
  useEffect(() => detachDragListeners, []);

  // 오버레이 총 높이 = 페이지 수 × (A4 높이 + gap). 페이지 2·3 필드가 스크롤로 도달 가능.
  const totalHeight = Math.max(1, pageCount) * (A4.HEIGHT_PX + PAGE_GAP);

  return (
    <div
      ref={rootRef}
      // 배경(rootRef div 자체) 은 pointer-events: none 이므로 배경 클릭은 편집기로 통과.
      // 필드 박스만 pointerEvents:"auto" 로 열어놨음 → 필드 밖 클릭 = 편집기 스크롤/무동작.
      style={{
        position: "absolute",
        inset: 0,
        width: A4.WIDTH_PX,
        height: totalHeight,
      }}
    >
      {/* 필드 박스들 — 페이지 오프셋 반영해 캔버스 전역 좌표에 배치 */}
      {fields.map((f) => {
        if (
          f.pageNo == null ||
          f.x == null ||
          f.y == null ||
          f.width == null ||
          f.height == null
        )
          return null;
        const canvasY = pageYToCanvasY(f.pageNo, f.y);
        const selected = selectedId === f.id;
        const color = fieldColor(f.type);
        return (
          <div
            key={f.id}
            data-field-id={f.id}
            style={{
              position: "absolute",
              left: f.x,
              top: canvasY,
              width: f.width,
              height: f.height,
              border: `2px ${selected ? "solid" : "dashed"} ${color}`,
              background: selected ? `${color}22` : `${color}11`,
              borderRadius: 4,
              boxSizing: "border-box",
              cursor: disabled ? "default" : "move",
              pointerEvents: "auto",
              userSelect: "none",
              touchAction: "none", // 모바일 브라우저 기본 스크롤/줌 차단
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              color,
              fontWeight: 600,
            }}
            onPointerDown={(e) => {
              if (disabled) return;
              // 리사이즈 핸들·삭제 버튼은 자체 stopPropagation 하므로 여기 안 옴
              e.stopPropagation();
              e.preventDefault();
              setSelectedId(f.id);
              beginDrag(e, {
                mode: "move",
                id: f.id,
                startClientX: e.clientX,
                startClientY: e.clientY,
                baseX: f.x ?? 0,
                baseY: f.y ?? 0,
                baseW: f.width ?? 0,
                baseH: f.height ?? 0,
                basePageNo: f.pageNo ?? 1,
              });
            }}
          >
            {/* 체크박스: 아이콘만 / 도장: 기본 도장 이미지(있으면) / 그 외: 아이콘+라벨 */}
            {f.type === "checkbox" ? (
              <span style={{ fontSize: 20 }}>{fieldIcon(f.type)}</span>
            ) : f.type === "seal" ? (
              (() => {
                const fSeal = resolveSeal(f);
                return fSeal ? (
                  <img
                    src={fSeal.imageUrl}
                    alt={fSeal.sealName || "사업장 도장"}
                    draggable={false}
                    style={{
                      maxWidth: "88%",
                      maxHeight: "88%",
                      objectFit: "contain",
                      pointerEvents: "none",
                      userSelect: "none",
                    }}
                  />
                ) : (
                  // 활성 도장 없음 — 발송 시 찍을 도장이 없다는 안내(연한 텍스트)
                  <span
                    style={{
                      fontSize: 9,
                      color: "#B0B0B0",
                      lineHeight: 1.2,
                      textAlign: "center",
                      padding: 2,
                    }}
                  >
                    도장
                    <br />
                    미등록
                  </span>
                );
              })()
            ) : (
              <>
                <span style={{ fontSize: 14, marginRight: 4 }}>
                  {fieldIcon(f.type)}
                </span>
                {f.label || f.key}
              </>
            )}

            {/* 리사이즈 핸들 — 우하단, 크게 잡을 수 있도록 20×20 */}
            {selected && !disabled && (
              <div
                style={{
                  position: "absolute",
                  right: -10,
                  bottom: -10,
                  width: 20,
                  height: 20,
                  background: color,
                  border: "2px solid #fff",
                  borderRadius: 4,
                  cursor: "nwse-resize",
                  touchAction: "none",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 700,
                }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  beginDrag(e, {
                    mode: "resize",
                    id: f.id,
                    startClientX: e.clientX,
                    startClientY: e.clientY,
                    baseX: f.x ?? 0,
                    baseY: f.y ?? 0,
                    baseW: f.width ?? 0,
                    baseH: f.height ?? 0,
                    basePageNo: f.pageNo ?? 1,
                  });
                }}
              >
                ↘
              </div>
            )}

            {/* 삭제 버튼 */}
            {selected && !disabled && (
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  deleteField(f.id);
                }}
                style={{
                  position: "absolute",
                  top: -10,
                  right: -10,
                  width: 20,
                  height: 20,
                  background: "#DC2626",
                  color: "#fff",
                  border: "none",
                  borderRadius: "50%",
                  fontSize: 12,
                  cursor: "pointer",
                  lineHeight: 1,
                }}
                aria-label="삭제"
              >
                ×
              </button>
            )}

            {/* 필수/선택 토글 — 필드를 선택하면 위에 떠서 바로 지정(기본=필수) */}
            {selected && !disabled && (
              <div
                onPointerDown={(e) => e.stopPropagation()}
                style={{
                  position: "absolute",
                  top: -8,
                  left: 0,
                  // 줌과 무관하게 일정 크기 — 캔버스 scale 을 역보정.
                  transform: `scale(${1 / Math.max(0.2, getZoom?.() ?? 1)})`,
                  transformOrigin: "left bottom",
                  display: "flex",
                  gap: 3,
                  background: "#fff",
                  border: "1px solid #E5E5E5",
                  borderRadius: 8,
                  padding: 3,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                  zIndex: 6,
                  whiteSpace: "nowrap",
                }}
              >
                <button
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    updateField(f.id, { required: true });
                  }}
                  style={{
                    height: 24,
                    padding: "0 12px",
                    border: "none",
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    background: f.required ? "#FF9934" : "transparent",
                    color: f.required ? "#fff" : "#999",
                  }}
                >
                  필수
                </button>
                <button
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    updateField(f.id, { required: false });
                  }}
                  style={{
                    height: 24,
                    padding: "0 12px",
                    border: "none",
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    background: !f.required ? "#EEE" : "transparent",
                    color: !f.required ? "#333" : "#999",
                  }}
                >
                  선택
                </button>
              </div>
            )}

            {/* 도장 변경 — 선택된 도장 필드에서 사용할 도장을 고른다(기본=기본 설정 도장) */}
            {selected &&
              !disabled &&
              f.type === "seal" &&
              activeSeals.length > 1 && (
                <button
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSealPickerFor(f.id);
                  }}
                  style={{
                    position: "absolute",
                    top: -12,
                    left: -2,
                    height: 20,
                    padding: "0 8px",
                    background: "#111",
                    color: "#fff",
                    border: "none",
                    borderRadius: 10,
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: "pointer",
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                  }}
                  aria-label="도장 변경"
                >
                  도장 변경 ▾
                </button>
              )}
          </div>
        );
      })}

      {/* 도장 선택 모달 — 필드에 사용할 도장 지정(디폴트=기본 설정 도장) */}
      {sealPickerFor &&
        (() => {
          const pf = fields.find((f) => f.id === sealPickerFor);
          if (!pf) return null;
          const currentSeal = resolveSeal(pf);
          return (
            <div
              className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => setSealPickerFor(null)}
            >
              <div
                className="bg-white rounded-t-3xl md:rounded-3xl w-full md:max-w-md p-5"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="text-[16px] font-bold mb-1">
                  사용할 도장 선택
                </div>
                <div className="text-[12px] text-[#666] mb-4">
                  이 계약서의 도장란에 찍힐 도장이에요. 기본값은 기본 설정
                  도장입니다.
                </div>
                {activeSeals.length === 0 ? (
                  <div className="rounded bg-[#FAFAFA] p-4 text-center text-[13px] text-[#999]">
                    등록된 활성 도장이 없어요. 도장 관리에서 먼저 등록해 주세요.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-[50vh] overflow-y-auto">
                    {activeSeals.map((s) => {
                      const picked = currentSeal?.sealSeq === s.sealSeq;
                      return (
                        <button
                          key={s.sealSeq}
                          type="button"
                          onClick={() => {
                            // 기본 도장을 고르면 defaultValue 를 비워 "기본 추종" 상태로 되돌린다.
                            updateField(pf.id, {
                              defaultValue:
                                s.defaultYn === "Y"
                                  ? undefined
                                  : String(s.sealSeq),
                            });
                            setSealPickerFor(null);
                          }}
                          className={`flex flex-col items-center gap-1 rounded-xl border p-3 ${
                            picked
                              ? "border-[#FF9934] bg-[#FFF7E6]"
                              : "border-[#E9E9E9] bg-white"
                          }`}
                        >
                          <img
                            src={s.imageUrl}
                            alt={s.sealName}
                            className="h-14 w-14 object-contain"
                            draggable={false}
                          />
                          <span className="text-[12px] text-[#333] truncate max-w-full">
                            {s.sealName}
                            {s.defaultYn === "Y" ? " (기본)" : ""}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setSealPickerFor(null)}
                  className="mt-4 w-full rounded-xl bg-[#F2F2F2] py-2.5 text-[13px] font-semibold text-[#666]"
                >
                  닫기
                </button>
              </div>
            </div>
          );
        })()}
    </div>
  );
}

/**
 * 툴바에서 필드 추가 — 현재 스크롤 뷰포트 중앙 위치의 페이지·좌표에 배치.
 * 사용자가 3페이지를 보고 있으면 3페이지에 필드가 뜨도록.
 */
export function makeAddField(
  fields: ContractField[],
  onChange: (f: ContractField[]) => void,
) {
  return (type: FieldType) => {
    const box = fieldDefaultBox(type);
    const key = defaultKey(type);

    // 현재 뷰포트의 스크롤 위치를 읽어 어느 페이지 중앙에 있는지 계산
    // PanZoomCanvas 의 scroll 컨테이너를 찾아 scrollTop 사용
    const { pageNo, y } = detectVisiblePagePosition();
    const placement = {
      pageNo,
      x: Math.round((A4.WIDTH_PX - box.w) / 2), // 페이지 가로 중앙
      y,
      width: box.w,
      height: box.h,
    };

    // 서명·도장·주소처럼 key 가 고정인 필드는 계약당 1개만 존재해야 한다.
    // 같은 type+key 필드가 이미 있으면(특히 AI 변환으로 생겼지만 좌표 미배치인 경우)
    // 새로 만들지 말고 그 필드를 현재 위치에 "배치"한다 — 중복 생성 → 저장 시 미배치본이
    // 배치본을 덮어써 반영이 안 되던 문제 방지.
    const existingIdx = fields.findIndex(
      (f) => f.type === type && f.key === key,
    );
    if (existingIdx >= 0) {
      onChange(
        fields.map((f, i) => (i === existingIdx ? { ...f, ...placement } : f)),
      );
      return;
    }

    const newField: ContractField = {
      id: `f-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type,
      key,
      label: defaultLabel(type),
      ...placement,
      auto: false,
      // 디폴트는 필수 — 배치 후 필드를 선택해 "선택"으로 바꿀 수 있음.
      required: true,
    };
    onChange([...fields, newField]);
  };
}

/** 현재 화면에 보이는 페이지·y 로컬 좌표 감지 — PanZoomCanvas scroll 컨테이너 참조. */
function detectVisiblePagePosition(): { pageNo: number; y: number } {
  // contract-page-stack (편집기 콘텐츠 스택) 을 찾아 부모 스크롤 컨테이너의 scrollTop 을 계산
  const stackEl = document.querySelector<HTMLElement>(".contract-page-stack");
  if (!stackEl) return { pageNo: 1, y: 200 };
  // PanZoomCanvas 의 스크롤 컨테이너 = 편집기 아래 첫 overflow: auto 조상
  let scrollEl: HTMLElement | null = stackEl.parentElement;
  while (scrollEl) {
    const style = window.getComputedStyle(scrollEl);
    if (style.overflowY === "auto" || style.overflowY === "scroll") break;
    scrollEl = scrollEl.parentElement;
  }
  if (!scrollEl) return { pageNo: 1, y: 200 };

  // 뷰포트 중앙의 화면 Y
  const scrollRect = scrollEl.getBoundingClientRect();
  const viewportCenterY = scrollRect.top + scrollRect.height / 2;
  // 스택 요소 (transform: scale 적용된 상태)
  const stackRect = stackEl.getBoundingClientRect();
  const naturalWidth = stackEl.offsetWidth || A4.WIDTH_PX;
  const zoomRatio = naturalWidth > 0 ? stackRect.width / naturalWidth : 1;
  const scaledPageStride = (A4.HEIGHT_PX + PAGE_GAP) * zoomRatio; // (1123 + PAGE_GAP) × zoom
  // 뷰포트 중앙이 스택 안 어디에 위치하는지 (스케일 반영된 좌표)
  const centerInStack = viewportCenterY - stackRect.top;
  if (centerInStack < 0) return { pageNo: 1, y: 200 };
  const pageIdx = Math.floor(centerInStack / Math.max(1, scaledPageStride));
  const pageNo = Math.max(1, pageIdx + 1);
  // 페이지 내부 로컬 y (스케일 되돌림)
  const localScaledY = centerInStack - pageIdx * scaledPageStride;
  const localY = Math.round(localScaledY / (zoomRatio > 0 ? zoomRatio : 1));
  const y = Math.max(75, Math.min(A4.HEIGHT_PX - 100, localY));
  return { pageNo, y };
}
